'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { useIsWebview } from '@flipster/features/src/shared/webview';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { IconButton } from '@flipster/react/design-system/icon-button';
import QrCodeIcon from '@flipster/react/icon/generated/QrCodeIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { hstack } from '@flipster/styled-system/patterns';
import segment, { marketingEventEvent } from '@flipster/utils/segment';
import { generateLinkWithQueryParams } from '@flipster/utils/url';
import webviewHandler from '@flipster/utils/webview-handler';

import { AppDownloadModal } from '@web/app/[locale]/_app-download/modal';

interface Props {
  sectionType: string;
  sectionIndex: number;
  json?: Record<'web' | 'app', Record<'notLoggedIn' | 'loggedIn', { text: string; url: string }>>;
}

const RenderEventButton = ({ sectionType, sectionIndex, json }: Props) => {
  const router = useRouter();
  const isMounted = useMounted();
  const isWebview = useIsWebview();
  const { isLoggedInAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const overlay = useOverlay();

  if (!isMounted || !json) return null;

  const currentJson = json[isWebview ? 'app' : 'web'][isLoggedIn ? 'loggedIn' : 'notLoggedIn'];

  const onClickLogging = () => {
    segment.track(marketingEventEvent.buttonClick, {
      section_type: sectionType,
      section_index: sectionIndex,
      button_text: currentJson.text,
      button_url: currentJson.url,
    });
  };

  if (currentJson.url === '/download-app')
    return (
      <div className={hstack({ textStyle: 'Text/M/Strong', color: 'content.default.level-3', gap: 16 })}>
        <span>Download app</span>
        <IconButton
          hierarchy='tertiary'
          size='large'
          icon={<QrCodeIcon />}
          onClick={() => {
            onClickLogging();
            overlay.open(<AppDownloadModal />);
          }}
        />
      </div>
    );
  return (
    <Button
      hierarchy='primary'
      color='accent'
      size='large'
      label={currentJson.text}
      onClick={() => {
        // marketing 코드와 중복!
        onClickLogging();
        const goLink = () => {
          const url = generateLinkWithQueryParams(currentJson.url, new URLSearchParams(window.location.search));
          const isExternal = url.startsWith('https');
          if (isExternal) window.open(url, '_blank', 'noopener');
          else router.push(url);
        };
        webviewHandler.postMessage(
          {
            action: 'link',
            params: { page: currentJson.url },
          },
          () => goLink(),
        );
      }}
    />
  );
};

export const EventButton = (props: Props) => {
  return (
    <Suspense>
      <RenderEventButton {...props} />
    </Suspense>
  );
};
